import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  NumberInput,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import {
  ApiError,
  fetchServiceConstraints,
  updateServiceInfo,
  updateServicePolicy,
  type ServiceConstraints,
  type Zone,
} from '../api-client.ts';
import { tokens } from '../ui/tokens.ts';

interface Props {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly zone: Zone;
  readonly venueId: string;
  readonly timezone: string;
  readonly onSaved: () => void;
}

interface BlackoutRow {
  readonly id: string;
  readonly start: Date | null;
  readonly end: Date | null;
}

function makeRow(): BlackoutRow {
  return { id: crypto.randomUUID(), start: null, end: null };
}

export function ServicePolicyModal({
  opened,
  onClose,
  zone,
  venueId,
  timezone,
  onSaved,
}: Props): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [constraints, setConstraints] = useState<ServiceConstraints | null>(null);

  const [minParty, setMinParty] = useState<number | ''>('');
  const [maxParty, setMaxParty] = useState<number | ''>('');
  const [durMin, setDurMin] = useState<number | ''>('');
  const [durMax, setDurMax] = useState<number | ''>('');
  const [leadTime, setLeadTime] = useState<number | ''>('');
  const [blackouts, setBlackouts] = useState<BlackoutRow[]>([]);
  const [overbook, setOverbook] = useState(false);
  const [split, setSplit] = useState(false);
  const [extension, setExtension] = useState(true);
  const [serviceName, setServiceName] = useState('');
  const [serviceActive, setServiceActive] = useState(true);

  useEffect(() => {
    if (!opened) return;
    setLoading(true);
    setErr(null);
    fetchServiceConstraints(venueId, zone.serviceId)
      .then((c) => {
        setConstraints(c);
        setMinParty(c.minPartySize);
        setMaxParty(c.maxPartySize);
        setDurMin(c.durationMinMinutes);
        setDurMax(c.durationMaxMinutes);
        setLeadTime(c.leadTimeMinutes);
        setBlackouts(
          c.blackoutWindows.map((w) => ({
            id: crypto.randomUUID(),
            start: new Date(w.start),
            end: new Date(w.end),
          })),
        );
        setOverbook(c.overbookAllowed);
        setSplit(c.splitAllowed);
        setExtension(c.extensionAllowed);
        setServiceName(c.serviceName);
        setServiceActive(c.active);
      })
      .catch((e) =>
        setErr(e instanceof ApiError ? `[${e.code}] ${e.message}` : String(e)),
      )
      .finally(() => setLoading(false));
  }, [opened, venueId, zone.serviceId]);

  async function handleSave(): Promise<void> {
    setErr(null);
    if (
      typeof minParty !== 'number' ||
      typeof maxParty !== 'number' ||
      typeof durMin !== 'number' ||
      typeof durMax !== 'number' ||
      typeof leadTime !== 'number'
    ) {
      setErr('請填寫所有數值');
      return;
    }
    if (minParty > maxParty) {
      setErr('最小人數不可大於最大人數');
      return;
    }
    if (durMin > durMax) {
      setErr('最短時長不可大於最長時長');
      return;
    }
    for (const b of blackouts) {
      if (!b.start || !b.end) {
        setErr('禁用時段的起訖時間都要填');
        return;
      }
      if (b.end.getTime() <= b.start.getTime()) {
        setErr('禁用時段的結束時間必須晚於開始');
        return;
      }
    }
    if (serviceName.trim().length === 0) {
      setErr('服務名稱不可為空');
      return;
    }
    setSaving(true);
    try {
      await updateServiceInfo(zone.serviceId, {
        name: serviceName.trim(),
        active: serviceActive,
      });
      await updateServicePolicy(zone.serviceId, {
        minPartySize: minParty,
        maxPartySize: maxParty,
        durationMinMinutes: durMin,
        durationMaxMinutes: durMax,
        leadTimeMinutes: leadTime,
        blackoutWindows: blackouts.map((b) => ({
          start: dayjs(b.start!).toISOString(),
          end: dayjs(b.end!).toISOString(),
        })),
        overbookAllowed: overbook,
        splitAllowed: split,
        extensionAllowed: extension,
      });
      notifications.show({
        color: 'green',
        title: '規則已更新',
        message: `${zone.zoneLabel} · ${zone.serviceName}`,
      });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? `[${e.code}] ${e.message}` : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`編輯服務規則 · ${zone.zoneLabel}`}
      centered
      size="lg"
    >
      {loading && <Loader />}
      {!loading && constraints && (
        <Stack gap="sm">
          <TextInput
            label="服務名稱"
            value={serviceName}
            onChange={(e) => setServiceName(e.currentTarget.value)}
            required
          />
          <Switch
            label="啟用服務"
            description="停用後，客人/新增訂位都無法選這個服務"
            checked={serviceActive}
            onChange={(e) => setServiceActive(e.currentTarget.checked)}
            color={tokens.color.mantine['primary']}
          />
          <Divider my="xs" />
          <Group grow>
            <NumberInput
              label="最小人數"
              value={minParty}
              onChange={(v) => setMinParty(typeof v === 'number' ? v : '')}
              min={1}
              required
            />
            <NumberInput
              label="最大人數"
              value={maxParty}
              onChange={(v) => setMaxParty(typeof v === 'number' ? v : '')}
              min={1}
              required
            />
          </Group>
          <Group grow>
            <NumberInput
              label="最短時長（分鐘）"
              value={durMin}
              onChange={(v) => setDurMin(typeof v === 'number' ? v : '')}
              min={1}
              step={15}
              required
            />
            <NumberInput
              label="最長時長（分鐘）"
              value={durMax}
              onChange={(v) => setDurMax(typeof v === 'number' ? v : '')}
              min={1}
              step={15}
              required
            />
          </Group>
          <NumberInput
            label="需提前多久訂位（分鐘）"
            description="0 表示不限制"
            value={leadTime}
            onChange={(v) => setLeadTime(typeof v === 'number' ? v : '')}
            min={0}
            step={15}
            required
          />

          <Group justify="space-between" mt="sm">
            <Text size="sm" fw={500}>
              禁用時段（{blackouts.length} 筆）
            </Text>
            <Button
              variant="light"
              size="xs"
              color={tokens.color.mantine['primary']}
              leftSection={<IconPlus size={14} />}
              onClick={() => setBlackouts((rows) => [...rows, makeRow()])}
            >
              新增
            </Button>
          </Group>
          {blackouts.length === 0 && (
            <Text size="xs" c="dimmed">
              沒有設定禁用時段
            </Text>
          )}
          <Stack gap={4}>
            {blackouts.map((b, i) => (
              <Group key={b.id} gap="xs" wrap="nowrap">
                <DateTimePicker
                  label={i === 0 ? '開始' : undefined}
                  value={b.start}
                  onChange={(v) =>
                    setBlackouts((rows) =>
                      rows.map((r) => (r.id === b.id ? { ...r, start: v } : r)),
                    )
                  }
                  placeholder={`當地時區 ${timezone}`}
                  flex={1}
                />
                <DateTimePicker
                  label={i === 0 ? '結束' : undefined}
                  value={b.end}
                  onChange={(v) =>
                    setBlackouts((rows) =>
                      rows.map((r) => (r.id === b.id ? { ...r, end: v } : r)),
                    )
                  }
                  placeholder={`當地時區 ${timezone}`}
                  flex={1}
                />
                <ActionIcon
                  color="red"
                  variant="subtle"
                  onClick={() =>
                    setBlackouts((rows) => rows.filter((r) => r.id !== b.id))
                  }
                  mt={i === 0 ? 22 : 0}
                  aria-label="刪除"
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>

          <Divider my="xs" />
          <Text size="sm" fw={500}>
            進階設定
          </Text>
          <Switch
            label="允許超額預訂（overbook）"
            description="預訂可超出 zone 容量；通常用於緩衝客戶 no-show"
            checked={overbook}
            onChange={(e) => setOverbook(e.currentTarget.checked)}
            color={tokens.color.mantine['primary']}
          />
          <Switch
            label="允許拆組（split）"
            description="人數不夠單 unit 時，自動拆到多個 unit"
            checked={split}
            onChange={(e) => setSplit(e.currentTarget.checked)}
            color={tokens.color.mantine['primary']}
          />
          <Switch
            label="允許延長時段（extension）"
            description="已建立的預訂可往後延；用於臨場加時"
            checked={extension}
            onChange={(e) => setExtension(e.currentTarget.checked)}
            color={tokens.color.mantine['primary']}
          />

          {err && (
            <Alert color="red" variant="light">
              {err}
            </Alert>
          )}
          <Group justify="flex-end" mt="sm">
            <Button variant="light" onClick={onClose} disabled={saving}>
              取消
            </Button>
            <Button
              color={tokens.color.mantine['primary']}
              onClick={() => void handleSave()}
              loading={saving}
            >
              儲存
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
